import React, { useState, useEffect } from 'react';
import { Users, UserPlus, FileUp, TrendingUp, RefreshCw } from 'lucide-react';
import { api } from '../../utils/api';
import type { Stats, ImportBatch, PaginatedResponse } from '../../types';

/* ------------------------------------------------------------------ */
/*  类型定义                                                            */
/* ------------------------------------------------------------------ */

interface DashboardStats extends Stats {
  todayNew: number;
}

/* ------------------------------------------------------------------ */
/*  骨架屏组件                                                          */
/* ------------------------------------------------------------------ */

const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse rounded-lg bg-slate-200 ${className}`} />
);

const CardSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl border border-slate-200 p-6">
    <div className="flex items-center justify-between mb-4">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-10 w-10 rounded-lg" />
    </div>
    <Skeleton className="h-8 w-20 mb-2" />
    <Skeleton className="h-3 w-32" />
  </div>
);

/* ------------------------------------------------------------------ */
/*  统计卡片组件                                                        */
/* ------------------------------------------------------------------ */

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
  bgColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color, bgColor }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow duration-200">
    <div className="flex items-center justify-between mb-4">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center ${color}`}>
        {icon}
      </div>
    </div>
    <div className="text-3xl font-bold text-slate-800 mb-1">{value}</div>
    <p className="text-xs text-slate-400">累计数据</p>
  </div>
);

/* ------------------------------------------------------------------ */
/*  柱状图组件（纯 CSS 实现）                                            */
/* ------------------------------------------------------------------ */

interface BarChartProps {
  data: { date: string; count: number }[];
}

const BarChart: React.FC<BarChartProps> = ({ data }) => {
  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="text-base font-semibold text-slate-800 mb-6">最近 7 天趋势</h3>
      <div className="flex items-end justify-between gap-2 sm:gap-4 h-48">
        {data.map((item) => {
          const heightPercent = (item.count / maxCount) * 100;
          return (
            <div key={item.date} className="flex-1 flex flex-col items-center gap-2">
              <span className="text-xs font-medium text-slate-600">{item.count}</span>
              <div className="w-full flex justify-center" style={{ height: '140px' }}>
                <div
                  className="w-full max-w-[40px] rounded-t-md bg-gradient-to-t from-blue-500 to-blue-400 transition-all duration-500 ease-out min-h-[4px]"
                  style={{ height: `${Math.max(heightPercent, 3)}%`, marginTop: 'auto' }}
                />
              </div>
              <span className="text-xs text-slate-400 whitespace-nowrap">
                {item.date.slice(5)}
              </span>
            </div>
          );
        })}
      </div>
      {data.length === 0 && (
        <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
          暂无数据
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  最近导入记录表格                                                     */
/* ------------------------------------------------------------------ */

interface BatchTableProps {
  batches: ImportBatch[];
}

const statusConfig: Record<string, { label: string; color: string }> = {
  completed: { label: '成功', color: 'bg-green-50 text-green-700 border-green-200' },
  processing: { label: '处理中', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  failed: { label: '失败', color: 'bg-red-50 text-red-700 border-red-200' },
};

const BatchTable: React.FC<BatchTableProps> = ({ batches }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6">
    <h3 className="text-base font-semibold text-slate-800 mb-4">最近导入记录</h3>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="text-left py-3 px-3 font-medium text-slate-500">文件名</th>
            <th className="text-left py-3 px-3 font-medium text-slate-500">类型</th>
            <th className="text-right py-3 px-3 font-medium text-slate-500">总数</th>
            <th className="text-right py-3 px-3 font-medium text-slate-500">成功</th>
            <th className="text-center py-3 px-3 font-medium text-slate-500">状态</th>
            <th className="text-left py-3 px-3 font-medium text-slate-500">时间</th>
          </tr>
        </thead>
        <tbody>
          {batches.map((batch) => {
            const status = statusConfig[batch.status] || statusConfig.completed;
            return (
              <tr key={batch.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                <td className="py-3 px-3 text-slate-800 font-medium truncate max-w-[200px]">
                  {batch.filename}
                </td>
                <td className="py-3 px-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                    {batch.file_type.toUpperCase()}
                  </span>
                </td>
                <td className="py-3 px-3 text-right text-slate-600">{batch.total_count}</td>
                <td className="py-3 px-3 text-right text-slate-600">{batch.success_count}</td>
                <td className="py-3 px-3 text-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${status.color}`}>
                    {status.label}
                  </span>
                </td>
                <td className="py-3 px-3 text-slate-400 text-xs">
                  {new Date(batch.created_at).toLocaleString('zh-CN', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </td>
              </tr>
            );
          })}
          {batches.length === 0 && (
            <tr>
              <td colSpan={6} className="py-12 text-center text-slate-400">
                暂无导入记录
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  数据来源分布组件                                                     */
/* ------------------------------------------------------------------ */

interface SourceDistributionProps {
  data: { source: string; count: number }[];
}

const sourceLabels: Record<string, string> = {
  manual: '手动添加',
  import: '文件导入',
};

const sourceColors: Record<string, string> = {
  manual: 'bg-blue-500',
  import: 'bg-emerald-500',
};

const SourceDistribution: React.FC<SourceDistributionProps> = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="text-base font-semibold text-slate-800 mb-6">数据来源分布</h3>
      {data.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
          暂无数据
        </div>
      ) : (
        <div className="space-y-4">
          {/* 总体进度条 */}
          <div className="h-3 rounded-full bg-slate-100 overflow-hidden flex">
            {data.map((item) => (
              <div
                key={item.source}
                className={`h-full ${sourceColors[item.source] || 'bg-slate-400'} transition-all duration-500`}
                style={{ width: `${total > 0 ? (item.count / total) * 100 : 0}%` }}
              />
            ))}
          </div>
          {/* 各来源明细 */}
          <div className="space-y-3">
            {data.map((item) => {
              const percent = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0';
              return (
                <div key={item.source} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-sm ${sourceColors[item.source] || 'bg-slate-400'}`} />
                    <span className="text-sm text-slate-600">
                      {sourceLabels[item.source] || item.source}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-slate-800">{item.count}</span>
                    <span className="text-xs text-slate-400 w-12 text-right">{percent}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  DashboardPage 主组件                                                */
/* ------------------------------------------------------------------ */

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [statsRes, batchesRes] = await Promise.all([
        api.get<DashboardStats>('/api/stats'),
        api.get<PaginatedResponse<ImportBatch>>('/api/batches?page=1&pageSize=5'),
      ]);

      setStats(statsRes);
      setBatches(batchesRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="space-y-6">
      {/* 顶部操作栏 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">数据概览</h2>
          <p className="text-sm text-slate-500 mt-0.5">查看系统运行数据和统计信息</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          刷新
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* 加载骨架屏 */}
      {loading && !stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Skeleton className="h-72" />
            </div>
            <Skeleton className="h-72" />
          </div>
          <Skeleton className="h-80" />
        </div>
      )}

      {/* 主内容 */}
      {!loading && stats && (
        <>
          {/* 统计卡片 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<Users className="w-5 h-5" />}
              label="总联系人数"
              value={stats.totalContacts}
              color="text-blue-600"
              bgColor="bg-blue-50"
            />
            <StatCard
              icon={<UserPlus className="w-5 h-5" />}
              label="本月新增"
              value={stats.monthlyNew}
              color="text-green-600"
              bgColor="bg-green-50"
            />
            <StatCard
              icon={<FileUp className="w-5 h-5" />}
              label="导入批次数"
              value={stats.totalBatches}
              color="text-purple-600"
              bgColor="bg-purple-50"
            />
            <StatCard
              icon={<TrendingUp className="w-5 h-5" />}
              label="今日新增"
              value={stats.todayNew ?? 0}
              color="text-orange-600"
              bgColor="bg-orange-50"
            />
          </div>

          {/* 图表区域 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <BarChart data={stats.dailyTrend || []} />
            </div>
            <SourceDistribution data={stats.bySource || []} />
          </div>

          {/* 最近导入记录 */}
          <BatchTable batches={batches} />
        </>
      )}
    </div>
  );
};

export default DashboardPage;
